import { useEffect, useState, useCallback } from "react";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import { Types } from "../store/reducer";
import { obterIdTenent, obterIdUsuario } from "../services/seguranca";
import api from "../services/api";
import { Campo } from "../services/tipos";
import { toast } from "react-toastify";
import { useFilialLote } from "../components/modal-selecionar-filial/FilialLoteProvider";

interface FilialOption {
    valor: string;
    label: string;
}

// F: só entidades INDEPENDENTES podem ter a filial atribuída diretamente em lote.
// Entidades dependentes do veículo (Locacao, Pagamento, Lancamento, Multa, ServicoVeiculo,
// TrocaDeOleo, Vistorias, PlanoManutencao) seguem a placa e mudam de filial apenas via cascade
// (backend recusa essas entidades em AssociarFilialEmLote). Mantenha em sincronia com
// FilialService.TabelaPorEntidade.
const ENTIDADES_FILIAL_DIRETA = [
    'Veiculo', 'Locatario', 'Peca', 'Estoque', 'PrestadorServico', 'ModeloContrato', 'Servico', 'Banco', 'Categoria',
];

/**
 * Hook que fornece as opções de filiais e o campo para formulários.
 * Se o usuário tem filiais atribuídas, mostra apenas as dele.
 * Se não tem, mostra todas da empresa.
 */
export function useFiliais() {
    const dispatch = useAppDispatch();
    const token = useAppSelector(state => state.token);
    const filialSelecionada = useAppSelector(state => state.filialSelecionada) || null;
    const [filiaisOptions, setFiliaisOptions] = useState<FilialOption[]>([]);
    const [empresaTemFiliais, setEmpresaTemFiliais] = useState(false);
    const [usuarioTemFilialAtribuida, setUsuarioTemFilialAtribuida] = useState(false);

    useEffect(() => {
        if (!token) {
            setFiliaisOptions([]);
            setEmpresaTemFiliais(false);
            return;
        }

        const carregar = async () => {
            try {
                // Busca todas da empresa (para saber se existem e para colunas do grid)
                const respEmpresa = await api.get(`/Filial/PorEmpresa/${obterIdTenent(token)}`);
                const todasFiliais = respEmpresa.data || [];
                const temFiliais = todasFiliais.length > 0;
                setEmpresaTemFiliais(temFiliais);
                dispatch({ type: Types.SET_FILIAIS, filiais: todasFiliais.map((f: any) => ({ id: f.id, nome: f.nome })) });

                if (!temFiliais) return;

                // Busca filiais do usuário com detalhes (inclui filialEstrita)
                const respUsuario = await api.get(`/Filial/PorUsuarioDetalhado/${obterIdUsuario(token)}`);
                const filiaisUser = (respUsuario.data || []).map((f: any) => ({ id: f.idFilial || f.id, nome: f.nomeFilial || f.nome }));

                // Verifica se o usuário tem filialEstrita habilitada
                const temFilialEstrita = (respUsuario.data || []).some((f: any) => f.filialEstrita === true);
                dispatch({ type: Types.SET_FILIAL_ESTRITA, filialEstrita: temFilialEstrita });

                // Se usuário tem filiais atribuídas, mostra só as dele nos forms e campo é obrigatório
                // Se não tem, mostra todas da empresa e campo é opcional
                setUsuarioTemFilialAtribuida(filiaisUser.length > 0);
                const filiaisParaForms = filiaisUser.length > 0 ? filiaisUser : todasFiliais;
                setFiliaisOptions(filiaisParaForms.map((f: any) => ({ valor: f.id, label: f.nome })));
            } catch {
                setFiliaisOptions([]);
                setEmpresaTemFiliais(false);
            }
        };

        carregar();
    }, [token, dispatch]);

    const getCampoFilial = (config?: { linha?: number; coluna?: number; largura?: number; onCampoDigitar?: (idFilial: string) => void }): Campo | null => {
        if (!empresaTemFiliais) return null;

        return {
            label: 'Unidade',
            nome: 'idFilial',
            tipo: 'select',
            required: usuarioTemFilialAtribuida,
            largura: config?.largura ?? 6,
            coluna: config?.coluna ?? 1,
            linha: config?.linha ?? 0,
            visible: true,
            opcoes: filiaisOptions,
            valorPadrao: filialSelecionada || undefined,
            ...(config?.onCampoDigitar ? { onCampoDigitar: config.onCampoDigitar } : {}),
        } as Campo;
    };

    const { abrirModalLote } = useFilialLote();

    /**
     * Abre o modal para selecionar a filial destino e associar em lote.
     */
    const associarFilialEmLote = useCallback((entidade: string, ids: (string | number)[], onSucesso?: () => void) => {
        if (!ENTIDADES_FILIAL_DIRETA.includes(entidade)) {
            // Dependente do veículo: a filial acompanha a placa (via cascade), não é atribuível direto.
            toast.warning('Esta entidade acompanha a unidade do veículo e não pode ser atribuída diretamente.');
            return;
        }
        if (!ids || ids.length === 0) {
            toast.warning('Selecione ao menos um registro.');
            return;
        }
        abrirModalLote(entidade, ids, filiaisOptions, onSucesso);
    }, [filiaisOptions, abrirModalLote]);

    /**
     * Retorna a ação em lote "Associar Filial" para usar em acoesEmLote do Listagem.
     * Retorna null para entidades dependentes do veículo (elas seguem a placa via cascade).
     */
    const getAcaoEmLoteFilial = (entidade: string, onSucesso?: () => void) => {
        if (!empresaTemFiliais || !ENTIDADES_FILIAL_DIRETA.includes(entidade)) return null;
        return {
            titulo: 'Associar Unidade',
            icone: require('react-icons/fa').FaBuilding,
            cor: '#1F467E',
            onClick: (itensSelecionados: (string | number)[]) => associarFilialEmLote(entidade, itensSelecionados, onSucesso),
        };
    };

    /**
     * Retorna o nome da filial pelo ID. Útil para exibir em labels de selects.
     */
    const getNomeFilial = useCallback((idFilial?: string | null): string => {
        if (!idFilial || !empresaTemFiliais) return '';
        const filial = filiaisOptions.find(f => f.valor === idFilial);
        return filial?.label || '';
    }, [filiaisOptions, empresaTemFiliais]);

    /**
     * Adiciona indicador de filial a um label. Ex: "QGB-8B53 - Toyota" → "QGB-8B53 - Toyota (Filial Centro)"
     */
    const labelComFilial = useCallback((label: string, idFilial?: string | null): string => {
        if (!idFilial || !empresaTemFiliais) return label;
        const nome = getNomeFilial(idFilial);
        return nome ? `${label} (${nome})` : label;
    }, [getNomeFilial, empresaTemFiliais]);

    return {
        empresaTemFiliais,
        usuarioTemFilialAtribuida,
        filiaisOptions,
        filialSelecionada,
        getCampoFilial,
        getAcaoEmLoteFilial,
        associarFilialEmLote,
        getNomeFilial,
        labelComFilial,
    };
}
